import { ArrowDownLeft, ArrowUpRight, Ban, ChevronDown, History, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pager } from '@/components/common/Pager'
import { EmptyState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime, fmtMoney } from '../../shared/format'
import { useMovements } from '../api'
import { useRequestVoid } from '../voids'
import type { AccountMovement, AccountPeriodSummary, FinAccount } from '../types'
import { useFinanceLookups } from '../ui'
import { Section } from './FinanceBits'

export type MovementFilter = { accountId: string; direction: 'all' | 'in' | 'out'; scope: 'period' | 'all' }

type Props = {
  title: string
  allLabel: string
  /** Yalnız bu hesapların hareketleri listelenir (sayfanın kapsamı). */
  accounts: FinAccount[]
  filter: MovementFilter
  onFilter: (f: MovementFilter) => void
  month: string
  periodLabel: string
  summary?: AccountPeriodSummary
  canWrite: boolean
}

/** Kasa/hesap hareketleri: giriş-çıkış ve dönem filtresi; seçili hesabın dönem özeti sunucudan gelir. */
export function MovementsSection({
  title,
  allLabel,
  accounts,
  filter,
  onFilter,
  month,
  periodLabel,
  summary,
  canWrite,
}: Props) {
  const { t } = useTranslation()
  const movements = useMovements()
  const voids = useRequestVoid()
  const { accountName, accountDetail, tx, catName } = useFinanceLookups()
  const [showVoided, setShowVoided] = useState(true)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const inScope = new Set(accounts.map((a) => a.id))
  const rows = (movements.data ?? []).filter(
    (m) =>
      inScope.has(m.accountId) &&
      (showVoided || !m.voided) &&
      (filter.accountId === 'all' || m.accountId === filter.accountId) &&
      (filter.direction === 'all' || m.direction === filter.direction) &&
      (filter.scope === 'all' || m.date.startsWith(month)),
  )

  const onDelete = (m: AccountMovement) => {
    const isEntry = m.source === 'entry'
    const description = t(isEntry ? 'finance.entry.deleteBody' : 'finance.accounts.transferDeleteBody', { amount: fmtMoney(m.amount), name: m.description })
    void voids.request(isEntry ? 'cash_entry' : 'transfer', m.refId, description)
  }

  const sourceLabel = (m: AccountMovement) => {
    if (m.source === 'payment') {
      const x = tx.get(m.refId)
      return x
        ? `${t(`finance.accounts.source.payment_${x.kind}`)} · ${catName(x.categoryId)}`
        : t('finance.accounts.source.payment')
    }
    return t(`finance.accounts.source.${m.source}`)
  }

  const { pageRows, pager } = usePagination(rows, [filter, month, showVoided])
  return (
    <Section
      title={title}
      icon={History}
      bodyClassName="p-0"
      actions={
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Tabs
            value={filter.direction}
            onValueChange={(v) => onFilter({ ...filter, direction: v as MovementFilter['direction'] })}
          >
            <TabsList size="sm">
              {(['all', 'in', 'out'] as const).map((d) => (
                <TabsTrigger key={d} value={d}>
                  {t(`finance.entry.filter.${d}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs whitespace-nowrap">
            <input type="checkbox" checked={showVoided} onChange={(e) => setShowVoided(e.target.checked)} />
            {t('finance.accounts.showVoided')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Select
              size="sm"
              className="sm:w-48"
              value={filter.accountId}
              onChange={(v) => onFilter({ ...filter, accountId: v })}
              aria-label={t('finance.accounts.filter')}
              options={[{ value: 'all', label: allLabel }, ...accounts.map((a) => ({ value: a.id, label: a.name }))]}
            />
            <Select
              size="sm"
              className="sm:w-40"
              value={filter.scope}
              onChange={(v) => onFilter({ ...filter, scope: v as MovementFilter['scope'] })}
              aria-label={t('finance.period')}
              options={[
                { value: 'period', label: periodLabel },
                { value: 'all', label: t('finance.allPeriods') },
              ]}
            />
          </div>
        </div>
      }
    >
      {summary && filter.accountId !== 'all' && filter.scope === 'period' ? (
        <div className="bg-muted/30 grid grid-cols-2 gap-3 border-b p-4 sm:grid-cols-4">
          {(['opening', 'inflow', 'outflow', 'closing'] as const).map((k) => (
            <div key={k}>
              <p className="text-muted-foreground text-xs">{t(`finance.accounts.summary.${k}`)}</p>
              <p
                className={cn(
                  'font-semibold tabular-nums',
                  k === 'inflow' && 'text-success',
                  k === 'outflow' && 'text-destructive',
                )}
              >
                {fmtMoney(summary[k])}
              </p>
            </div>
          ))}
        </div>
      ) : null}
      {movements.isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={History} title={t('finance.accounts.noMovements')} />
      ) : (
        <>
          <ul className="divide-y">
            {pageRows.map((m) => {
              const isIn = m.direction === 'in'
              const deletable =
                canWrite && !m.voided && (m.source === 'entry' || (m.source === 'transfer' && m.direction === 'out'))
              // Aktarımın iki bacağı aynı kimliği taşır; satır anahtarı yön ve hesapla ayrışır.
              const key = `${m.id}-${m.direction}-${m.accountId}`
              const open = openKey === key
              return (
                <li key={key} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3', m.voided && 'bg-muted/30')}>
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-full',
                      isIn ? 'bg-success/12 text-success' : 'bg-destructive/10 text-destructive',
                    )}
                  >
                    {isIn ? (
                      <ArrowDownLeft className="size-4" aria-hidden />
                    ) : (
                      <ArrowUpRight className="size-4" aria-hidden />
                    )}
                  </span>
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      <span className={cn('truncate', m.voided && 'text-muted-foreground line-through')}>
                        {m.description || t('finance.accounts.source.transfer')}
                      </span>
                      {m.voided ? (
                        <Badge tone="danger">
                          <Ban className="size-3" aria-hidden />
                          {t('finance.accounts.voided')}
                        </Badge>
                      ) : null}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                      {fmtDate(m.date)} · {accountName(m.accountId)}
                      {accountDetail(m.accountId) ? ` (${accountDetail(m.accountId)})` : ''} · {sourceLabel(m)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'font-semibold tabular-nums',
                      m.voided ? 'text-muted-foreground line-through' : isIn ? 'text-success' : 'text-destructive',
                    )}
                  >
                    {isIn ? '+' : '−'}
                    {fmtMoney(m.amount)}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-expanded={open}
                    aria-label={t('finance.accounts.details')}
                    onClick={() => setOpenKey(open ? null : key)}
                  >
                    <ChevronDown className={cn('transition-transform', open && 'rotate-180')} />
                  </Button>
                  {deletable ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="hover:text-destructive size-8"
                      aria-label={t('common.delete')}
                      onClick={() => void onDelete(m)}
                    >
                      <Trash2 />
                    </Button>
                  ) : (
                    <span className="size-8" aria-hidden />
                  )}
                  {open ? <MovementTrail m={m} /> : null}
                </li>
              )
            })}
          </ul>
          <Pager {...pager} />
        </>
      )}
    </Section>
  )
}

/** Hareketin izi: kim oluşturdu, iptal edildiyse kim talep etti, kim onayladı, ne zaman ve neden. */
function MovementTrail({ m }: { m: AccountMovement }) {
  const { t } = useTranslation()
  const row = (label: string, value: string) =>
    value ? (
      <>
        <dt className="text-muted-foreground">{label}</dt>
        <dd>{value}</dd>
      </>
    ) : null
  return (
    <dl className="bg-background grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border p-3 text-xs">
      {row(t('finance.accounts.trail.createdBy'), m.createdBy)}
      {row(t('finance.accounts.trail.createdAt'), fmtDateTime(m.createdAt))}
      {m.voided ? (
        <>
          {row(t('finance.accounts.trail.voidRequestedBy'), m.voidRequestedBy)}
          {row(t('finance.accounts.trail.voidReason'), m.voidReason)}
          {row(t('finance.accounts.trail.voidedBy'), m.voidedBy)}
          {row(t('finance.accounts.trail.voidedAt'), fmtDateTime(m.voidedAt))}
          {row(t('finance.accounts.trail.voidNote'), m.voidDecisionNote)}
          <dd className="text-muted-foreground col-span-2 pt-1">{t('finance.accounts.trail.voidEffect')}</dd>
        </>
      ) : null}
    </dl>
  )
}
